import { z } from 'zod';
import { CurrencySchema, DecimalSchema, LocaleSchema } from './common';
import { FiscalProfileSchema } from './fiscal';

/**
 * Tenant-scoped settings, stored as key/value JSON so a new setting never
 * requires a migration. Known keys are listed in {@link SETTING_KEYS}; the
 * API accepts unknown keys so a newer client can persist its own.
 */
export const SETTING_KEYS = ['branding', 'fiscal', 'numbering', 'stock'] as const;

export const TenantSettingSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  updatedAt: z.string(),
});
export type TenantSetting = z.infer<typeof TenantSettingSchema>;

export const UpdateSettingSchema = z.object({
  value: z.unknown(),
});
export type UpdateSetting = z.infer<typeof UpdateSettingSchema>;

/** Shape of the `branding` setting. */
export const BrandingSettingSchema = z.object({
  displayName: z.string().max(200).optional(),
  locale: LocaleSchema.optional(),
  logoUrl: z.string().max(2000).nullable().optional(),
  /** ISO-4217 code used for every monetary value shown in the interface. */
  currency: CurrencySchema.optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, { message: 'validation.format' })
    .optional(),
});
export type BrandingSetting = z.infer<typeof BrandingSettingSchema>;

/**
 * Shape of the `numbering` setting: a document-number prefix per document kind,
 * e.g. `{ RECEIPT: 'RE', ISSUE: 'RS' }`. Absent kinds fall back to the API
 * default, so the record is intentionally open-ended.
 */
export const NumberingSettingSchema = z.record(z.string(), z.string().max(10));
export type NumberingSetting = z.infer<typeof NumberingSettingSchema>;

/** Shape of the `fiscal` setting: the company's Argentine tax identity. */
export const FiscalSettingSchema = FiscalProfileSchema;
export type FiscalSetting = z.infer<typeof FiscalSettingSchema>;

/** Shape of the `stock` setting. */
export const StockSettingSchema = z.object({
  /** Whether an outbound document may drive the balance below zero. */
  allowNegative: z.boolean().optional(),
  /**
   * Costing method. Only `movingAverage` is implemented; the field exists so
   * the choice is recorded in data rather than implied by the engine.
   */
  valuation: z.enum(['movingAverage']).optional(),
  /** Default reorder threshold applied to products without their own. */
  lowStockThreshold: DecimalSchema.optional(),
});
export type StockSetting = z.infer<typeof StockSettingSchema>;
